import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import {
  Users,
  Plus,
  Loader2,
  Edit,
  Trash2,
  Upload,
  Search,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  ArrowRight,
  Download,
  X,
  RefreshCw,
  Phone,
  MessageCircle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  ArrowUp,
  ArrowDown,
  ArrowUpDown
} from "lucide-react";

export default function WhatsAppContactosPage() {
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGrupo, setSelectedGrupo] = useState("");
  const [selectedClase, setSelectedClase] = useState("");
  const [grupos, setGrupos] = useState([]);
  const [clasesCliente, setClasesCliente] = useState([]);
  const [total, setTotal] = useState(0);
  const [conWhatsapp, setConWhatsapp] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [sortBy, setSortBy] = useState("");
  const [sortDir, setSortDir] = useState("asc");
  
  // Dialog states
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  
  // Import states
  const [importStep, setImportStep] = useState(1); // 1: upload, 2: mapping, 3: preview, 4: importing, 5: done
  const [isDraggingExcel, setIsDraggingExcel] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewCampos, setPreviewCampos] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [deletingSelected, setDeletingSelected] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importData, setImportData] = useState(null);
  const [columnMapping, setColumnMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Form state
  const [contactForm, setContactForm] = useState({
    nombre: "",
    apellido: "",
    direccion: "",
    telefono: "",
    whatsapp: "",
    supervisor: "",
    clase_cliente: "",
    grupo: "",
    contacto: ""
  });
  
  const fileInputRef = useRef(null);
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes((user?.role || "").toLowerCase());

  const fetchContacts = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (searchTerm) params.append("search", searchTerm);
      if (selectedGrupo) params.append("grupo", selectedGrupo);
      if (selectedClase) params.append("clase_cliente", selectedClase);
      params.append("skip", String((page - 1) * pageSize));
      params.append("limit", String(pageSize));
      if (sortBy) {
        params.append("sort_by", sortBy);
        params.append("sort_dir", sortDir);
      }
      
      const res = await axios.get(`${API}/whatsapp/contacts?${params}`, getAuthHeaders());
      setContacts(res.data.contacts || []);
      setTotal(res.data.total || 0);
      setConWhatsapp(res.data.con_whatsapp || 0);
      setGrupos(res.data.grupos || []);
      setClasesCliente(res.data.clases_cliente || []);
    } catch (err) {
      console.error("Error fetching contacts:", err);
      toast.error("Error al cargar contactos");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, searchTerm, selectedGrupo, selectedClase, page, pageSize, sortBy, sortDir]);

  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, selectedGrupo, selectedClase, pageSize, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleSort = (campo) => {
    if (sortBy === campo) {
      setSortDir(d => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(campo);
      setSortDir("asc");
    }
  };

  const handleSaveContact = async () => {
    if (!contactForm.nombre || !contactForm.whatsapp) {
      toast.error("Nombre y WhatsApp son requeridos");
      return;
    }

    try {
      if (editingContact) {
        await axios.put(`${API}/whatsapp/contacts/${editingContact.id}`, contactForm, getAuthHeaders());
        toast.success("Contacto actualizado");
      } else {
        await axios.post(`${API}/whatsapp/contacts`, contactForm, getAuthHeaders());
        toast.success("Contacto creado");
      }
      setShowContactDialog(false);
      resetContactForm();
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar contacto");
    }
  };

  const handleDeleteContact = async (contact) => {
    if (!confirm(`¿Eliminar contacto "${contact.nombre}"?`)) return;
    
    try {
      await axios.delete(`${API}/whatsapp/contacts/${contact.id}`, getAuthHeaders());
      toast.success("Contacto eliminado");
      fetchContacts();
    } catch (err) {
      toast.error("Error al eliminar contacto");
    }
  };

  const openEditContact = (contact) => {
    setEditingContact(contact);
    setContactForm({
      nombre: contact.nombre || "",
      apellido: contact.apellido || "",
      direccion: contact.direccion || "",
      telefono: contact.telefono || "",
      whatsapp: contact.whatsapp || "",
      supervisor: contact.supervisor || "",
      clase_cliente: contact.clase_cliente || "",
      grupo: contact.grupo || "",
      contacto: contact.contacto || ""
    });
    setShowContactDialog(true);
  };

  const resetContactForm = () => {
    setEditingContact(null);
    setContactForm({
      nombre: "",
      apellido: "",
      direccion: "",
      telefono: "",
      whatsapp: "",
      supervisor: "",
      clase_cliente: "",
      grupo: "",
      contacto: ""
    });
  };

  // Import functions
  const processExcelFile = async (file) => {
    if (!file) return;

    const nombre = file.name.toLowerCase();
    if (!nombre.endsWith('.xlsx') && !nombre.endsWith('.xls') && !nombre.endsWith('.csv')) {
      toast.error("Por favor seleccione un archivo Excel (.xlsx, .xls) o CSV (.csv)");
      return;
    }

    setImportFile(file);
    setImportStep(2);

    // Read and send file
    const reader = new FileReader();
    reader.onload = async (event) => {
      try {
        const base64 = event.target.result.split(',')[1];
        
        const formData = new URLSearchParams();
        formData.append('file', base64);
        formData.append('filename', file.name);
        
        const res = await axios.post(`${API}/whatsapp/contacts/upload-excel`, formData, {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            'Content-Type': 'application/x-www-form-urlencoded'
          }
        });
        
        setImportData(res.data);
        setColumnMapping(res.data.suggested_mapping || {});
        
      } catch (err) {
        console.error("Upload error:", err);
        toast.error(err.response?.data?.detail || "Error al procesar archivo");
        setImportStep(1);
      }
    };
    reader.readAsDataURL(file);
  };

  const handleFileSelect = (e) => {
    processExcelFile(e.target.files[0]);
  };

  const handleExcelDrop = (e) => {
    e.preventDefault();
    setIsDraggingExcel(false);
    processExcelFile(e.dataTransfer?.files?.[0]);
  };

  const handlePreview = async () => {
    if (!importData?.file_id) return;
    if (!Object.values(columnMapping).includes("whatsapp")) {
      toast.error("Debe mapear al menos la columna de WhatsApp");
      return;
    }
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/whatsapp/contacts/preview`, {
        file_id: importData.file_id,
        mapping: columnMapping
      }, getAuthHeaders());
      const orden = ["nombre", "apellido", "whatsapp", "telefono", "direccion", "supervisor", "clase_cliente", "grupo", "contacto"];
      const mapeados = [...new Set(Object.values(columnMapping))];
      setPreviewCampos(orden.filter(c => mapeados.includes(c)));
      setPreviewRows(res.data.rows || []);
      setImportStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al generar la vista previa");
    } finally {
      setLoadingPreview(false);
    }
  };

  const setPreviewCell = (rowIdx, campo, valor) => {
    setPreviewRows(rows => rows.map((r, i) => (i === rowIdx ? { ...r, [campo]: valor } : r)));
  };

  const removePreviewRow = (rowIdx) => {
    setPreviewRows(rows => rows.filter((_, i) => i !== rowIdx));
  };

  const handleImport = async () => {
    const validas = previewRows.filter(r => (r.whatsapp || "").trim() && !r._duplicado);
    if (validas.length === 0) {
      toast.error("No hay contactos válidos para importar");
      return;
    }
    setImporting(true);
    setImportStep(4);
    
    try {
      const res = await axios.post(`${API}/whatsapp/contacts/import-rows`, {
        file_id: importData?.file_id,
        rows: previewRows.map(({ _duplicado, ...r }) => r)
      }, getAuthHeaders());
      
      setImportResult(res.data);
      setImportStep(5);
      fetchContacts();
      
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al importar");
      setImportStep(3);
    } finally {
      setImporting(false);
    }
  };

  const resetImport = () => {
    setImportStep(1);
    setImportFile(null);
    setImportData(null);
    setColumnMapping({});
    setImportResult(null);
    setPreviewRows([]);
    setPreviewCampos([]);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === contacts.length && contacts.length > 0) {
      setSelectedIds([]);
    } else {
      setSelectedIds(contacts.map(c => c.id));
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds(ids => ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id]);
  };

  const handleDeleteSelected = async () => {
    if (selectedIds.length === 0) return;
    if (!window.confirm(`¿Eliminar ${selectedIds.length} contacto(s) seleccionado(s)? Esta acción no se puede deshacer.`)) return;
    setDeletingSelected(true);
    try {
      const res = await axios.post(`${API}/whatsapp/contacts/delete-selected`, { ids: selectedIds }, getAuthHeaders());
      toast.success(`${res.data.deleted} contacto(s) eliminado(s)`);
      setSelectedIds([]);
      fetchContacts();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al eliminar los contactos");
    } finally {
      setDeletingSelected(false);
    }
  };

  const closeImportDialog = () => {
    setShowImportDialog(false);
    resetImport();
  };

  const dbFieldLabels = {
    nombre: "Nombre",
    apellido: "Apellido",
    direccion: "Dirección",
    telefono: "Teléfono",
    whatsapp: "WhatsApp *",
    supervisor: "Supervisor",
    clase_cliente: "Clase Cliente",
    grupo: "Grupo",
    contacto: "Contacto"
  };

  return (
    <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 overflow-auto p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <Users className="w-6 h-6 text-green-600" />
                Contactos WhatsApp
              </h1>
              <p className="text-slate-500">Gestione su base de datos de contactos para WhatsApp</p>
            </div>
            <div className="flex gap-2">
              {isAdmin && (
                <Button variant="outline" onClick={() => setShowImportDialog(true)} data-testid="import-excel-btn">
                  <Upload className="w-4 h-4 mr-2" />
                  Importar Excel
                </Button>
              )}
              <Button onClick={() => { resetContactForm(); setShowContactDialog(true); }} data-testid="new-contact-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nuevo Contacto
              </Button>
            </div>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Users className="w-8 h-8 text-blue-500" />
                  <div>
                    <p className="text-2xl font-bold">{total}</p>
                    <p className="text-sm text-slate-500">Total Contactos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <MessageCircle className="w-8 h-8 text-green-500" />
                  <div>
                    <p className="text-2xl font-bold">{grupos.length}</p>
                    <p className="text-sm text-slate-500">Grupos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Badge className="w-8 h-8 text-purple-500" />
                  <div>
                    <p className="text-2xl font-bold">{clasesCliente.length}</p>
                    <p className="text-sm text-slate-500">Clases de Cliente</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="pt-6">
                <div className="flex items-center gap-3">
                  <Phone className="w-8 h-8 text-orange-500" />
                  <div>
                    <p className="text-2xl font-bold" data-testid="stat-con-whatsapp">{conWhatsapp}</p>
                    <p className="text-sm text-slate-500">Con WhatsApp</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Filters */}
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-wrap gap-4">
                <div className="flex-1 min-w-[200px]">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <Input
                      placeholder="Buscar por nombre, teléfono o WhatsApp..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                </div>
                <Select value={selectedGrupo || "all"} onValueChange={(v) => setSelectedGrupo(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrar por grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los grupos</SelectItem>
                    {grupos.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={selectedClase || "all"} onValueChange={(v) => setSelectedClase(v === "all" ? "" : v)}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filtrar por clase" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las clases</SelectItem>
                    {clasesCliente.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button variant="outline" size="icon" onClick={fetchContacts}>
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Contacts Table */}
          <Card>
            <CardHeader>
              <CardTitle>Lista de Contactos</CardTitle>
              <CardDescription className="flex items-center gap-3 flex-wrap">
                <span>Total: {total} contactos</span>
                {isAdmin && selectedIds.length > 0 && (
                  <Button size="sm" variant="destructive" onClick={handleDeleteSelected} disabled={deletingSelected} data-testid="delete-selected-btn">
                    {deletingSelected ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Trash2 className="w-4 h-4 mr-1" />}
                    Eliminar seleccionados ({selectedIds.length})
                  </Button>
                )}
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : (
                <div className="space-y-3">
                  <div className="border rounded-lg overflow-auto max-h-[62vh]" data-testid="contacts-table-container">
                    <Table className="border-separate border-spacing-0 [&_td]:border-b [&_td]:border-r [&_td]:border-slate-200 dark:[&_td]:border-slate-700 [&_td:last-child]:border-r-0">
                      <TableHeader>
                        <TableRow className="hover:bg-transparent">
                          {isAdmin && (
                            <TableHead className="w-10 sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 border-b border-r border-slate-200 dark:border-slate-700">
                              <input type="checkbox" className="w-4 h-4 accent-green-600 cursor-pointer"
                                checked={contacts.length > 0 && selectedIds.length === contacts.length}
                                onChange={toggleSelectAll} data-testid="select-all-contacts" />
                            </TableHead>
                          )}
                          {[
                            { label: "Nombre", campo: "nombre" },
                            { label: "Apellido", campo: "apellido" },
                            { label: "WhatsApp", campo: "whatsapp" },
                            { label: "Teléfono", campo: "telefono" },
                            { label: "Grupo", campo: "grupo" },
                            { label: "Clase Cliente", campo: "clase_cliente" },
                            { label: "Supervisor", campo: "supervisor" }
                          ].map(({ label, campo }) => (
                            <TableHead key={campo}
                              className="sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 border-b border-r border-slate-200 dark:border-slate-700 cursor-pointer select-none hover:bg-slate-200 dark:hover:bg-slate-700"
                              onClick={() => toggleSort(campo)} data-testid={`sort-header-${campo}`}>
                              <span className="flex items-center gap-1">
                                {label}
                                {sortBy === campo ? (
                                  sortDir === "asc" ? <ArrowUp className="w-3.5 h-3.5 text-green-600" /> : <ArrowDown className="w-3.5 h-3.5 text-green-600" />
                                ) : (
                                  <ArrowUpDown className="w-3 h-3 text-slate-400" />
                                )}
                              </span>
                            </TableHead>
                          ))}
                          <TableHead className="text-center sticky top-0 z-10 bg-slate-100 dark:bg-slate-800 font-semibold text-slate-700 dark:text-slate-200 border-b border-slate-200 dark:border-slate-700">Acciones</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {contacts.map(contact => (
                          <TableRow key={contact.id} className={selectedIds.includes(contact.id) ? "bg-green-50/60 dark:bg-green-900/10" : "odd:bg-white even:bg-slate-50/60 dark:odd:bg-transparent dark:even:bg-slate-800/30"}>
                            {isAdmin && (
                              <TableCell>
                                <input type="checkbox" className="w-4 h-4 accent-green-600 cursor-pointer"
                                  checked={selectedIds.includes(contact.id)}
                                  onChange={() => toggleSelect(contact.id)} data-testid={`select-contact-${contact.id}`} />
                              </TableCell>
                            )}
                            <TableCell className="font-medium">{contact.nombre}</TableCell>
                            <TableCell>{contact.apellido || "-"}</TableCell>
                            <TableCell>
                              {(contact.whatsapp || "").trim() ? (
                                <button
                                  type="button"
                                  onClick={() => navigate(`/whatsapp/bandeja?nuevo=${encodeURIComponent(contact.whatsapp)}`)}
                                  title="Iniciar conversación de WhatsApp con este contacto"
                                  data-testid={`iniciar-conv-${contact.id}`}
                                  className="focus:outline-none"
                                >
                                  <Badge variant="outline" className="gap-1 cursor-pointer hover:bg-green-50 hover:border-green-500 hover:text-green-700 dark:hover:bg-green-900/20 transition-colors">
                                    <MessageCircle className="w-3 h-3 text-green-600" />
                                    {contact.whatsapp}
                                  </Badge>
                                </button>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            <TableCell>{contact.telefono || "-"}</TableCell>
                            <TableCell>{contact.grupo || "-"}</TableCell>
                            <TableCell>{contact.clase_cliente || "-"}</TableCell>
                            <TableCell>{contact.supervisor || "-"}</TableCell>
                            <TableCell>
                              <div className="flex justify-center gap-1">
                                <Button size="icon" variant="ghost" onClick={() => openEditContact(contact)} data-testid={`edit-contact-${contact.id}`}>
                                  <Edit className="w-4 h-4" />
                                </Button>
                                {isAdmin && (
                                  <Button size="icon" variant="ghost" className="text-red-500" onClick={() => handleDeleteContact(contact)} data-testid={`delete-contact-${contact.id}`}>
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                        {contacts.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={isAdmin ? 9 : 8} className="text-center py-8 text-slate-500">
                              No hay contactos. {isAdmin ? "Importe un Excel o agregue" : "Agregue"} contactos manualmente.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Paginación */}
                  <div className="flex items-center justify-between flex-wrap gap-3" data-testid="contacts-pagination">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Mostrar</span>
                      <Select value={String(pageSize)} onValueChange={(v) => setPageSize(parseInt(v, 10))}>
                        <SelectTrigger className="w-24 h-8" data-testid="page-size-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {[25, 50, 100, 200, 500].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <span className="text-sm text-slate-500">
                        contactos · {total === 0 ? 0 : (page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} de {total}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(1)} data-testid="page-first-btn">
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 px-2" disabled={page <= 1} onClick={() => setPage(p => p - 1)} data-testid="page-prev-btn">
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="text-sm px-2" data-testid="page-indicator">Página {page} de {totalPages}</span>
                      <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)} data-testid="page-next-btn">
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" className="h-8 px-2" disabled={page >= totalPages} onClick={() => setPage(totalPages)} data-testid="page-last-btn">
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Contact Dialog */}
        <Dialog open={showContactDialog} onOpenChange={(open) => { setShowContactDialog(open); if (!open) resetContactForm(); }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingContact ? "Editar Contacto" : "Nuevo Contacto"}</DialogTitle>
              <DialogDescription>Complete los datos del contacto de WhatsApp</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div>
                <Label>Nombre *</Label>
                <Input value={contactForm.nombre} onChange={(e) => setContactForm({ ...contactForm, nombre: e.target.value })} placeholder="Nombre" />
              </div>
              <div>
                <Label>Apellido</Label>
                <Input value={contactForm.apellido} onChange={(e) => setContactForm({ ...contactForm, apellido: e.target.value })} placeholder="Apellido" />
              </div>
              <div>
                <Label>WhatsApp *</Label>
                <Input value={contactForm.whatsapp} onChange={(e) => setContactForm({ ...contactForm, whatsapp: e.target.value })} placeholder="+1809XXXXXXX" />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input value={contactForm.telefono} onChange={(e) => setContactForm({ ...contactForm, telefono: e.target.value })} placeholder="809-XXX-XXXX" />
              </div>
              <div className="col-span-2">
                <Label>Dirección</Label>
                <Input value={contactForm.direccion} onChange={(e) => setContactForm({ ...contactForm, direccion: e.target.value })} placeholder="Dirección completa" />
              </div>
              <div>
                <Label>Supervisor</Label>
                <Input value={contactForm.supervisor} onChange={(e) => setContactForm({ ...contactForm, supervisor: e.target.value })} placeholder="Nombre del supervisor" />
              </div>
              <div>
                <Label>Contacto</Label>
                <Input value={contactForm.contacto} onChange={(e) => setContactForm({ ...contactForm, contacto: e.target.value })} placeholder="Info adicional de contacto" />
              </div>
              <div>
                <Label>Grupo</Label>
                <Input value={contactForm.grupo} onChange={(e) => setContactForm({ ...contactForm, grupo: e.target.value })} placeholder="Grupo o categoría" />
              </div>
              <div>
                <Label>Clase Cliente</Label>
                <Input value={contactForm.clase_cliente} onChange={(e) => setContactForm({ ...contactForm, clase_cliente: e.target.value })} placeholder="Ej: VIP, Regular" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowContactDialog(false)}>Cancelar</Button>
              <Button onClick={handleSaveContact}>{editingContact ? "Guardar Cambios" : "Crear Contacto"}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={showImportDialog} onOpenChange={(open) => { if (!open) closeImportDialog(); else setShowImportDialog(true); }}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FileSpreadsheet className="w-5 h-5 text-green-600" />
                Importar Contactos desde Excel
              </DialogTitle>
              <DialogDescription>
                {importStep === 1 && "Suba un archivo Excel o CSV con los datos de sus contactos"}
                {importStep === 2 && "Revise y ajuste el mapeo de columnas sugerido por la IA"}
                {importStep === 3 && "Vista previa: revise y corrija los contactos antes de importar"}
                {importStep === 4 && "Importando contactos..."}
                {importStep === 5 && "Importación completada"}
              </DialogDescription>
            </DialogHeader>

            {/* Progress Steps */}
            <div className="flex items-center justify-between mb-6">
              {[1, 2, 3, 4, 5].map(step => (
                <div key={step} className="flex items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                    importStep >= step ? "bg-green-600 text-white" : "bg-slate-200 text-slate-500"
                  }`}>
                    {importStep > step ? <CheckCircle className="w-5 h-5" /> : step}
                  </div>
                  {step < 5 && <div className={`w-14 h-1 ${importStep > step ? "bg-green-600" : "bg-slate-200"}`} />}
                </div>
              ))}
            </div>

            {/* Step 1: Upload */}
            {importStep === 1 && (
              <div className="space-y-4">
                <div
                  className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                    isDraggingExcel ? "border-green-500 bg-green-50 dark:bg-green-900/20" : "border-slate-300"
                  }`}
                  onDragOver={(e) => {
                    if (e.dataTransfer?.types?.includes("Files")) {
                      e.preventDefault();
                      setIsDraggingExcel(true);
                    }
                  }}
                  onDragLeave={(e) => {
                    if (!e.currentTarget.contains(e.relatedTarget)) setIsDraggingExcel(false);
                  }}
                  onDrop={handleExcelDrop}
                  data-testid="excel-drop-zone"
                >
                  <FileSpreadsheet className={`w-12 h-12 mx-auto mb-4 ${isDraggingExcel ? "text-green-500" : "text-slate-400"}`} />
                  <p className="text-lg font-medium mb-2">
                    {isDraggingExcel ? "Suelte el archivo aquí" : "Arrastre su archivo Excel o CSV aquí"}
                  </p>
                  <p className="text-sm text-slate-500 mb-4">
                    o selecciónelo manualmente · Formatos: .xlsx, .xls, .csv
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".xlsx,.xls,.csv"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button onClick={() => fileInputRef.current?.click()}>
                    <Upload className="w-4 h-4 mr-2" />
                    Seleccionar Archivo
                  </Button>
                </div>
                <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                  <p className="text-sm font-medium text-blue-800 dark:text-blue-200">Campos esperados:</p>
                  <p className="text-sm text-blue-600 dark:text-blue-300">
                    Nombre, Apellido, Dirección, Teléfono, WhatsApp, Supervisor, Clase Cliente, Grupo, Contacto
                  </p>
                  <p className="text-xs text-blue-500 dark:text-blue-400 mt-2">
                    La IA intentará mapear automáticamente las columnas de su Excel a estos campos.
                  </p>
                </div>
              </div>
            )}

            {/* Step 2: Mapping */}
            {importStep === 2 && importData && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{importFile?.name}</p>
                    <p className="text-sm text-slate-500">{importData.total_rows} filas encontradas</p>
                  </div>
                  <Badge variant="outline" className="gap-1">
                    <CheckCircle className="w-3 h-3 text-green-500" />
                    Mapeo sugerido por IA
                  </Badge>
                </div>

                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead>Columna del Excel</TableHead>
                        <TableHead>Mapear a Campo</TableHead>
                        <TableHead>Ejemplo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {importData.headers.map(header => (
                        <TableRow key={header}>
                          <TableCell className="font-medium">{header}</TableCell>
                          <TableCell>
                            <Select 
                              value={columnMapping[header] || "_none"} 
                              onValueChange={(v) => {
                                const newMapping = { ...columnMapping };
                                if (v === "_none") {
                                  delete newMapping[header];
                                } else {
                                  newMapping[header] = v;
                                }
                                setColumnMapping(newMapping);
                              }}
                            >
                              <SelectTrigger className="w-[180px]">
                                <SelectValue placeholder="No mapear" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">No mapear</SelectItem>
                                {importData.db_fields.map(field => (
                                  <SelectItem key={field} value={field}>
                                    {dbFieldLabels[field] || field}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </TableCell>
                          <TableCell className="text-slate-500 text-sm">
                            {importData.sample_data[0]?.[header] || "-"}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {!Object.values(columnMapping).includes("whatsapp") && (
                  <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-yellow-600" />
                    <span className="text-sm text-yellow-800 dark:text-yellow-200">
                      Debe mapear al menos la columna de WhatsApp
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <Button variant="outline" onClick={resetImport}>
                    <X className="w-4 h-4 mr-2" />
                    Cancelar
                  </Button>
                  <Button onClick={handlePreview} disabled={!Object.values(columnMapping).includes("whatsapp") || loadingPreview} data-testid="preview-btn">
                    {loadingPreview && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    Vista Previa
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Preview editable */}
            {importStep === 3 && (
              <div className="space-y-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex gap-2 flex-wrap">
                    <Badge variant="outline">{previewRows.length} contactos</Badge>
                    <Badge className="bg-green-600">{previewRows.filter(r => (r.whatsapp || "").trim() && !r._duplicado).length} se importarán</Badge>
                    {previewRows.filter(r => !(r.whatsapp || "").trim()).length > 0 && (
                      <Badge variant="destructive">{previewRows.filter(r => !(r.whatsapp || "").trim()).length} sin WhatsApp</Badge>
                    )}
                    {previewRows.filter(r => r._duplicado).length > 0 && (
                      <Badge className="bg-amber-500">{previewRows.filter(r => r._duplicado).length} duplicados (se omiten)</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">Puede corregir cualquier celda antes de importar</p>
                </div>

                <div className="border rounded-lg overflow-auto max-h-[45vh]">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-slate-50 dark:bg-slate-800">
                        <TableHead className="w-8">#</TableHead>
                        {previewCampos.map(campo => (
                          <TableHead key={campo}>{dbFieldLabels[campo] || campo}{campo === "whatsapp" ? " *" : ""}</TableHead>
                        ))}
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody data-testid="preview-table">
                      {previewRows.map((row, idx) => {
                        const sinWa = !(row.whatsapp || "").trim();
                        return (
                          <TableRow key={idx} className={sinWa ? "bg-red-50 dark:bg-red-900/10" : row._duplicado ? "bg-amber-50 dark:bg-amber-900/10" : ""}>
                            <TableCell className="text-xs text-slate-400">
                              {idx + 1}
                              {row._duplicado && <Badge className="bg-amber-500 text-[10px] ml-1 px-1">dup</Badge>}
                            </TableCell>
                            {previewCampos.map(campo => (
                              <TableCell key={campo} className="p-1">
                                <Input
                                  value={row[campo] || ""}
                                  onChange={(e) => setPreviewCell(idx, campo, e.target.value)}
                                  className={`h-8 text-sm min-w-[110px] ${campo === "whatsapp" && sinWa ? "border-red-400" : ""}`}
                                  data-testid={`preview-cell-${idx}-${campo}`}
                                />
                              </TableCell>
                            ))}
                            <TableCell className="p-1">
                              <Button size="icon" variant="ghost" className="text-red-500 h-7 w-7" onClick={() => removePreviewRow(idx)} title="Quitar fila" data-testid={`preview-remove-${idx}`}>
                                <X className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>

                <div className="flex justify-between">
                  <Button variant="outline" onClick={() => setImportStep(2)}>
                    Volver al Mapeo
                  </Button>
                  <Button onClick={handleImport} disabled={importing || previewRows.filter(r => (r.whatsapp || "").trim() && !r._duplicado).length === 0} data-testid="import-rows-btn">
                    Importar {previewRows.filter(r => (r.whatsapp || "").trim() && !r._duplicado).length} Contactos
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Importing */}
            {importStep === 4 && (
              <div className="py-8 text-center">
                <Loader2 className="w-12 h-12 animate-spin text-green-600 mx-auto mb-4" />
                <p className="text-lg font-medium">Importando contactos...</p>
                <p className="text-slate-500">Por favor espere mientras procesamos el archivo</p>
              </div>
            )}

            {/* Step 5: Done */}
            {importStep === 5 && importResult && (
              <div className="space-y-4">
                <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg text-center">
                  <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-4" />
                  <p className="text-xl font-bold text-green-800 dark:text-green-200">Importación Completada</p>
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-green-600">{importResult.imported}</p>
                      <p className="text-sm text-slate-500">Importados</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-yellow-600">{importResult.skipped}</p>
                      <p className="text-sm text-slate-500">Omitidos</p>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardContent className="pt-6 text-center">
                      <p className="text-3xl font-bold text-red-600">{importResult.errors?.length || 0}</p>
                      <p className="text-sm text-slate-500">Errores</p>
                    </CardContent>
                  </Card>
                </div>

                {importResult.errors?.length > 0 && (
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg">
                    <p className="font-medium text-red-800 dark:text-red-200 mb-2">Errores:</p>
                    <ul className="text-sm text-red-600 dark:text-red-300 space-y-1">
                      {importResult.errors.map((err, idx) => (
                        <li key={idx}>• {err}</li>
                      ))}
                    </ul>
                  </div>
                )}

                <div className="flex justify-end">
                  <Button onClick={closeImportDialog}>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Cerrar
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
